// IE Hub - Color Check Page
// Comprehensive theme color testing and preview

import { useState } from 'react';
import {
  Box,
  Chip,
  Container,
  Divider,
  Grid,
  Paper,
  Stack,
  Typography,
  useTheme
} from '../ui';
import { TextField, UIButton } from '../ui';
import { 
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  Palette as PaletteIcon,
  Warning as WarningIcon
} from '@mui/icons-material';

export default function CheckColor(): JSX.Element {
  const theme = useTheme();
  const [textValue, setTextValue] = useState('Sample text');

  // Color palette data
  const paletteColors = [
    { name: 'Primary', color: theme.palette.primary, usage: 'Main brand color, buttons, links' },
    { name: 'Secondary', color: theme.palette.secondary, usage: 'Accent color, highlights' },
    { name: 'Success', color: theme.palette.success, usage: 'Success states, confirmations' },
    { name: 'Warning', color: theme.palette.warning, usage: 'Warnings, cautions' },
    { name: 'Error', color: theme.palette.error, usage: 'Errors, validation failures' },
    { name: 'Info', color: theme.palette.info, usage: 'Information, tips' }
  ];

  const ColorSwatch = ({ name, color, usage }: { name: string; color: any; usage: string }) => (
    <Paper elevation={1} sx={{ p: 3, borderRadius: 2 }}>
      <Stack spacing={2}>
        <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
          {name}
        </Typography>
        
        {/* Color swatches */}
        <Stack direction="row" spacing={1} alignItems="center">
          <Box
            sx={{
              width: 48,
              height: 48,
              backgroundColor: color.main,
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'divider'
            }}
          />
          <Box
            sx={{
              width: 32,
              height: 32,
              backgroundColor: color.light,
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'divider'
            }}
          />
          <Box
            sx={{
              width: 32,
              height: 32,
              backgroundColor: color.dark,
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'divider'
            }}
          />
        </Stack>

        {/* Color values */}
        <Stack spacing={0.5}>
          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
            <strong>Main:</strong> {color.main}
          </Typography>
          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
            <strong>Light:</strong> {color.light}
          </Typography>
          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
            <strong>Dark:</strong> {color.dark}
          </Typography>
        </Stack>

        <Typography variant="body2" color="text.secondary">
          {usage}
        </Typography>
      </Stack>
    </Paper>
  );

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4, textAlign: 'center' }}>
        <PaletteIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
        <Typography variant="h3" sx={{ fontWeight: 700, mb: 1 }}>
          Theme Color Check
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Comprehensive preview of all theme colors and components
        </Typography>
        <Chip 
          label={`Client: ${(theme as any).ieHub?.client || 'default'}`}
          color="primary"
          sx={{ mt: 2 }}
        />
      </Box>

      <Stack spacing={4}>
        {/* Color Palette */}
        <Box>
          <Typography variant="h4" sx={{ mb: 3, fontWeight: 600 }}>
            🎨 Color Palette
          </Typography>
          <Grid container spacing={3}>
            {paletteColors.map((item) => (
              <Grid item xs={12} md={6} lg={4} key={item.name}>
                <ColorSwatch {...item} />
              </Grid>
            ))}
          </Grid>
        </Box>

        <Divider />

        {/* Interactive Components */}
        <Box>
          <Typography variant="h4" sx={{ mb: 3, fontWeight: 600 }}>
            🧩 Interactive Components
          </Typography>
          
          <Grid container spacing={3}>
            {/* Text Fields */}
            <Grid item xs={12} md={6}>
              <Paper elevation={1} sx={{ p: 3, borderRadius: 2 }}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  Text Fields
                </Typography>
                <Stack spacing={2}>
                  <TextField
                    label="Default TextField"
                    value={textValue}
                    onChange={(e) => setTextValue(e.target.value)}
                    helperText="Check the focus border color"
                  />
                  <TextField
                    label="Required Field"
                    validation={{ required: true }}
                    error
                    helperText="Error state with theme error color"
                  />
                  <TextField
                    label="Disabled Field"
                    disabled
                    value="Disabled state"
                  />
                </Stack>
              </Paper>
            </Grid>

            {/* Buttons */}
            <Grid item xs={12} md={6}>
              <Paper elevation={1} sx={{ p: 3, borderRadius: 2 }}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  Buttons
                </Typography>
                <Stack spacing={2}>
                  <Stack direction="row" spacing={1} flexWrap="wrap">
                    <UIButton variant="contained" color="primary">
                      Primary
                    </UIButton>
                    <UIButton variant="contained" color="secondary">
                      Secondary
                    </UIButton>
                    <UIButton variant="outlined" color="primary">
                      Outlined
                    </UIButton>
                  </Stack>
                  <Stack direction="row" spacing={1} flexWrap="wrap">
                    <UIButton variant="contained" color="success">
                      Success
                    </UIButton>
                    <UIButton variant="contained" color="warning">
                      Warning
                    </UIButton>
                    <UIButton variant="contained" color="error">
                      Error
                    </UIButton>
                  </Stack>
                </Stack>
              </Paper>
            </Grid>

            {/* Status Indicators */}
            <Grid item xs={12} md={6}>
              <Paper elevation={1} sx={{ p: 3, borderRadius: 2 }}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  Status Indicators
                </Typography>
                <Stack spacing={2}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <CheckIcon color="success" />
                    <Typography>Success - Operation completed</Typography>
                  </Stack>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <WarningIcon color="warning" />
                    <Typography>Warning - Attention required</Typography>
                  </Stack>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <ErrorIcon color="error" />
                    <Typography>Error - Something went wrong</Typography>
                  </Stack>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <InfoIcon color="info" />
                    <Typography>Info - Additional information</Typography>
                  </Stack>
                </Stack>
              </Paper>
            </Grid>

            {/* Progress Indicators */}
            <Grid item xs={12} md={6}>
              <Paper elevation={1} sx={{ p: 3, borderRadius: 2 }}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  Progress Indicators
                </Typography>
                <Stack spacing={2}>
                  {/* Progress bar like in OnBoarding */}
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Box sx={{ width: 16, height: 8, bgcolor: 'primary.main', borderRadius: 999 }} />
                    <Typography variant="body2">Primary Progress</Typography>
                  </Stack>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Box sx={{ width: 16, height: 8, bgcolor: 'secondary.main', borderRadius: 999 }} />
                    <Typography variant="body2">Secondary Progress</Typography>
                  </Stack>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Box sx={{ width: 16, height: 8, bgcolor: 'success.main', borderRadius: 999 }} />
                    <Typography variant="body2">Success Progress</Typography>
                  </Stack>
                </Stack>
              </Paper>
            </Grid>
          </Grid>
        </Box>

        <Divider />

        {/* Typography Showcase */}
        <Box>
          <Typography variant="h4" sx={{ mb: 3, fontWeight: 600 }}>
            📝 Typography Scale
          </Typography>
          <Paper elevation={1} sx={{ p: 3, borderRadius: 2 }}>
            <Stack spacing={2}>
              <Typography variant="h1" color="primary.main">H1 Heading</Typography>
              <Typography variant="h2" color="text.primary">H2 Heading</Typography>
              <Typography variant="h3" color="text.primary">H3 Heading</Typography>
              <Typography variant="h4" color="text.primary">H4 Heading</Typography>
              <Typography variant="h5" color="text.primary">H5 Heading</Typography>
              <Typography variant="h6" color="text.primary">H6 Heading</Typography>
              <Typography variant="body1" color="text.primary">
                Body 1 - Lorem ipsum dolor sit amet, consectetur adipiscing elit.
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Body 2 - Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Caption - Small text for additional information
              </Typography>
            </Stack>
          </Paper>
        </Box>

        {/* Theme Info */}
        <Box>
          <Typography variant="h4" sx={{ mb: 3, fontWeight: 600 }}>
            🔧 Theme Configuration
          </Typography>
          <Paper elevation={1} sx={{ p: 3, borderRadius: 2 }}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  Current Theme
                </Typography>
                <Stack spacing={1}>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                    <strong>Client:</strong> {(theme as any).ieHub?.client || 'default'}
                  </Typography>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                    <strong>Version:</strong> {(theme as any).ieHub?.version || 'N/A'}
                  </Typography>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                    <strong>Border Radius:</strong> {theme.shape.borderRadius}px
                  </Typography>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                    <strong>Spacing Unit:</strong> {theme.spacing(1)}
                  </Typography>
                </Stack>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  Brand Colors
                </Typography>
                <Stack spacing={1}>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                    <strong>Primary:</strong> {theme.palette.primary.main}
                  </Typography>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                    <strong>Secondary:</strong> {theme.palette.secondary.main}
                  </Typography>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                    <strong>Background:</strong> {theme.palette.background.default}
                  </Typography>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                    <strong>Text Primary:</strong> {theme.palette.text.primary}
                  </Typography>
                </Stack>
              </Grid>
            </Grid>
          </Paper>
        </Box>
      </Stack>
    </Container>
  );
}
